import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/** The command-queue stub the UXCam web SDK drains once it finishes downloading. */
interface UxcQueue {
  __t: any[][];
  __ak: string;
  __o: Record<string, any>;
  event(name: string, params?: Record<string, any>): void;
  setUserIdentity(id: string): void;
  setUserProperty(key: string, value: any): void;
  setUserProperties(props: Record<string, any>): void;
}

declare global {
  interface Window {
    uxc?: UxcQueue;
  }
}

/**
 * Session recording (UXCam), loaded so it can never sit in front of the menu.
 *
 * The splash screen already fans out to device login, client data, the menu payload, the i18n
 * bundle and Firebase before a diner sees anything, so a third-party recorder must not join that
 * queue. Two things keep it out of the way:
 *
 *  - the vendor stub (`window.uxc`) is installed synchronously and is pure in-memory bookkeeping —
 *    it costs nothing and lets `event()` / `setShop()` be called from anywhere at any time. Calls
 *    made before the SDK lands are buffered in `__t` and replayed by the SDK on arrival, so no
 *    caller has to care whether the recorder is up yet.
 *  - the ~SDK download itself is deferred to the first idle slice *after* `window.load`, so it
 *    competes with nothing on the critical path. `crossorigin=anonymous` + `async`/`defer` mirror
 *    the vendor snippet.
 *
 * A missing key (the default outside production) makes the whole service a no-op, and a failed
 * download stays silent: nothing here is allowed to surface as an app error.
 */
@Injectable({ providedIn: 'root' })
export class UxCamService {
  private started = false;

  /**
   * Installs the queue stub and schedules the SDK download. Idempotent; called once from
   * AppComponent at bootstrap.
   */
  init(): void {
    if (this.started || !environment.uxcamAppKey || typeof window === 'undefined') {
      return;
    }
    this.started = true;

    this.installStub(environment.uxcamAppKey);

    // Identify what we already know at bootstrap (a refresh / direct-nav has both in storage
    // before splash re-runs). Everything else arrives through setShop() once splash resolves it.
    const deviceId = localStorage.getItem('deviceGuid');
    if (deviceId) {
      this.setUserIdentity(deviceId);
    }
    this.setShop(sessionStorage.getItem('clientId'));

    this.whenIdle(() => this.injectSdk());
  }

  /** Records a custom event. Safe before the SDK loads (queued) and when disabled (dropped). */
  event(name: string, params?: Record<string, any>): void {
    try {
      window.uxc?.event(name, params);
    } catch (e) {}
  }

  setUserIdentity(id: string): void {
    try {
      window.uxc?.setUserIdentity(id);
    } catch (e) {}
  }

  setUserProperty(key: string, value: any): void {
    try {
      window.uxc?.setUserProperty(key, value);
    } catch (e) {}
  }

  setUserProperties(props: Record<string, any>): void {
    try {
      window.uxc?.setUserProperties(props);
    } catch (e) {}
  }

  /**
   * Tags the session with the venue. Named `venue_id` to match AnalyticsService — see the note
   * there on why the shop does not travel as `client_id`.
   */
  setShop(shopId: string | null | undefined): void {
    if (!shopId) return;
    this.setUserProperty('venue_id', String(shopId));
  }

  /** The vendor snippet's stub, verbatim in behaviour: buffer calls until the SDK drains `__t`. */
  private installStub(appKey: string): void {
    if (window.uxc) return;

    window.uxc = {
      __t: [],
      __ak: appKey,
      __o: {},
      event(name: string, params?: Record<string, any>) {
        this.__t.push(['event', name, params]);
      },
      setUserIdentity(id: string) {
        this.__t.push(['setUserIdentity', id]);
      },
      setUserProperty(key: string, value: any) {
        this.__t.push(['setUserProperty', key, value]);
      },
      setUserProperties(props: Record<string, any>) {
        this.__t.push(['setUserProperties', props]);
      }
    };
  }

  private injectSdk(): void {
    if (document.getElementById('uxcam-web-sdk')) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://websdk-recording.uxcam.com/index.js';
    script.async = true;
    script.defer = true;
    script.id = 'uxcam-web-sdk';
    script.crossOrigin = 'anonymous';
    // A blocked or offline recorder is not an app problem: swallow it and leave the queue unread.
    script.onerror = () => {};
    document.head.appendChild(script);
  }

  /**
   * Runs `fn` once the page has settled: after `load`, in an idle slice. The `timeout` (and the
   * setTimeout fallback for Safari, which shipped requestIdleCallback only in 16.4) guarantees it
   * still runs on a page that never goes idle.
   */
  private whenIdle(fn: () => void): void {
    const schedule = () => {
      if (typeof (window as any).requestIdleCallback === 'function') {
        (window as any).requestIdleCallback(fn, { timeout: 3000 });
      } else {
        window.setTimeout(fn, 2000);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }
}
